/**
 * Contact-specific validators for form validation
 */

/**
 * Validates email address format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates that a name contains only letters, spaces, hyphens, and apostrophes
 */
export const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  return nameRegex.test(name);
};

/**
 * Validates that required fields are not empty
 */
export const validateRequired = (value: string | undefined | null): boolean => {
  return (
    value !== undefined && value !== null && value.toString().trim() !== ''
  );
};

/**
 * Validates date format (YYYY-MM-DD)
 */
export const validateDateFormat = (date: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) return false;

  const parsedDate = new Date(date);
  return !isNaN(parsedDate.getTime());
};

/**
 * Validates that a date is not in the future
 */
export const validateNotFutureDate = (date: string): boolean => {
  const inputDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return inputDate <= today;
};

/**
 * Validates minimum age requirement
 */
export const validateMinimumAge = (
  dateOfBirth: string,
  minimumAge: number = 18
): boolean => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age >= minimumAge;
};

/**
 * Validates string length constraints
 */
export const validateLength = (
  value: string,
  minLength?: number,
  maxLength?: number
): boolean => {
  const length = value.trim().length;
  if (minLength !== undefined && length < minLength) return false;
  if (maxLength !== undefined && length > maxLength) return false;
  return true;
};

/**
 * Validates that at least one field in a group has a value
 */
export const validateAtLeastOne = (
  values: (string | undefined | null)[]
): boolean => {
  return values.some((value) => validateRequired(value));
};

/**
 * Collection of all contact validators for easy import
 */
export const contactValidators = {
  email: validateEmail,
  name: validateName,
  required: validateRequired,
  dateFormat: validateDateFormat,
  notFutureDate: validateNotFutureDate,
  minimumAge: validateMinimumAge,
  length: validateLength,
  atLeastOne: validateAtLeastOne
};
